/**
 * Validating operation args against an operation's `inputSchema` (spec 021).
 * Implements exactly the subset `JsonSchema` declares; every keyword outside
 * it is rejected by `schemaKeywordErrors`, so a schema can never rely on a
 * keyword this validator ignores.
 */
import type { JsonSchema, OperationError } from './types';

const KEYWORDS = new Set([
  'type', 'description', 'properties', 'required', 'additionalProperties', 'items', 'minItems', 'enum', 'anyOf',
]);
const TYPES = new Set(['object', 'string', 'number', 'integer', 'boolean', 'array']);

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function typeOf(v: unknown): string {
  if (Array.isArray(v)) return 'array';
  if (v === null) return 'null';
  return typeof v;
}

function matchesType(type: JsonSchema['type'], v: unknown): boolean {
  switch (type) {
    case undefined: return true;
    case 'object': return isPlainObject(v);
    case 'array': return Array.isArray(v);
    case 'string': return typeof v === 'string';
    case 'boolean': return typeof v === 'boolean';
    case 'number': return typeof v === 'number' && Number.isFinite(v);
    case 'integer': return typeof v === 'number' && Number.isInteger(v);
    default: return false;
  }
}

const join = (path: string, key: string | number) =>
  typeof key === 'number' ? `${path}[${key}]` : path ? `${path}.${key}` : key;

/** One `anyOf` branch in words: `"none"`, `an object with type`, `a number`. */
function formText(s: JsonSchema): string {
  if (s.enum) return s.enum.map((v) => JSON.stringify(v)).join(' or ');
  if (s.anyOf) return s.anyOf.map(formText).join(' or ');
  if (s.type === 'object') return s.required?.length ? `an object with ${s.required.join(', ')}` : 'an object';
  return s.type === 'integer' ? 'an integer' : s.type === 'array' ? 'an array' : `a ${s.type}`;
}

/** What `value` was, for an error: the string itself, or `a string holding JSON` when it encodes an object or array. */
function gotText(value: unknown): string {
  if (typeof value !== 'string') return `got ${typeOf(value)}`;
  if (/^\s*[[{]/.test(value)) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) return `got a string holding JSON; pass the ${Array.isArray(parsed) ? 'array' : 'object'} itself, not text`;
    } catch { /* not JSON: show it as it is */ }
  }
  return `got the string ${JSON.stringify(value.length > 40 ? `${value.slice(0, 40)}…` : value)}`;
}

/** Every way `value` violates `schema` (empty when it conforms). */
export function validateAgainstSchema(schema: JsonSchema, value: unknown, path = ''): OperationError[] {
  const err = (message: string, at = path): OperationError[] => [{ code: 'invalid_args', message, path: at || '(args)' }];

  if (schema.anyOf) {
    const branchErrors = schema.anyOf.map((s) => validateAgainstSchema(s, value, path));
    if (branchErrors.some((e) => e.length === 0)) return [];
    const forms = () => err(`${path || 'args'} must be ${schema.anyOf!.map(formText).join(', or ')}, ${gotText(value)}`);
    // Report the branch the value was evidently meant for: the one of its type.
    // A lone enum branch of its type (`"none"` beside an object) is no evidence:
    // name every form, or "must be one of "none"" reads as "fade is not allowed".
    const sameType = schema.anyOf
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.type !== undefined && matchesType(s.type, value));
    if (sameType.length === 1) return sameType[0].s.enum ? forms() : branchErrors[sameType[0].i];
    if (sameType.length > 1) {
      // Several objects: the branch whose required keys are all present.
      const keyed = sameType.filter(({ s }) =>
        isPlainObject(value) && (s.required ?? []).every((k) => k in value));
      if (keyed.length === 1) return branchErrors[keyed[0].i];
    }
    return forms();
  }

  if (!matchesType(schema.type, value)) {
    return err(`${path || 'args'} must be ${formText(schema)}, ${gotText(value)}`);
  }
  if (schema.enum && !schema.enum.includes(value as string | number | boolean)) {
    return err(`${path || 'args'} must be one of ${schema.enum.map((e) => JSON.stringify(e)).join(', ')}`);
  }
  if (schema.type === 'array') {
    const arr = value as unknown[];
    const out: OperationError[] = [];
    if (schema.minItems !== undefined && arr.length < schema.minItems) {
      out.push(...err(`${path || 'args'} must have at least ${schema.minItems} item(s)`));
    }
    if (schema.items) arr.forEach((item, i) => out.push(...validateAgainstSchema(schema.items!, item, join(path, i))));
    return out;
  }
  if (schema.type === 'object') {
    const obj = value as Record<string, unknown>;
    const props = schema.properties ?? {};
    const out: OperationError[] = [];
    for (const key of schema.required ?? []) {
      if (obj[key] === undefined) out.push(...err(`${join(path, key)} is required`, join(path, key)));
    }
    for (const [key, v] of Object.entries(obj)) {
      if (v === undefined) continue;
      const sub = props[key];
      if (!sub) {
        if (schema.additionalProperties === false) out.push(...err(`${join(path, key)} is not a known field`, join(path, key)));
        continue;
      }
      out.push(...validateAgainstSchema(sub, v, join(path, key)));
    }
    return out;
  }
  return [];
}

/**
 * Structural problems with a schema itself (empty when it is well-formed):
 * unknown keywords, unknown types, an object without
 * `additionalProperties: false` or with an untyped property, `required` keys
 * that are not properties. Used by the registry test (spec 021 AC-1).
 */
export function schemaKeywordErrors(schema: JsonSchema, path = '#'): string[] {
  const out: string[] = [];
  if (!isPlainObject(schema as unknown)) return [`${path}: not an object`];
  for (const key of Object.keys(schema)) if (!KEYWORDS.has(key)) out.push(`${path}: unknown keyword ${key}`);
  if (schema.anyOf) {
    if (!Array.isArray(schema.anyOf) || schema.anyOf.length < 2) out.push(`${path}: anyOf needs two or more branches`);
    schema.anyOf.forEach((s, i) => out.push(...schemaKeywordErrors(s, `${path}/anyOf/${i}`)));
    if (schema.type) out.push(`${path}: anyOf with a type`);
    return out;
  }
  if (!schema.type || !TYPES.has(schema.type)) out.push(`${path}: missing or unknown type`);
  if (schema.enum !== undefined && (!Array.isArray(schema.enum) || schema.enum.length === 0)) out.push(`${path}: empty enum`);
  if (schema.type === 'object') {
    if (schema.additionalProperties !== false) out.push(`${path}: additionalProperties must be false`);
    const props = schema.properties ?? {};
    for (const [k, s] of Object.entries(props)) out.push(...schemaKeywordErrors(s, `${path}/properties/${k}`));
    for (const r of schema.required ?? []) if (!(r in props)) out.push(`${path}: required ${r} is not a property`);
  } else if (schema.properties || schema.required || schema.additionalProperties !== undefined) {
    out.push(`${path}: object keywords on a ${schema.type}`);
  }
  if (schema.type === 'array') {
    if (!schema.items) out.push(`${path}: array without items`);
    else out.push(...schemaKeywordErrors(schema.items, `${path}/items`));
  } else if (schema.items || schema.minItems !== undefined) {
    out.push(`${path}: array keywords on a ${schema.type}`);
  }
  if (schema.minItems !== undefined && schema.minItems !== 0 && schema.minItems !== 1) out.push(`${path}: minItems above 1`);
  return out;
}
